import {Icon, Show, ThemeIconKey} from 'platform/foundation';
import {cn, cva} from 'platform/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

export type BadgeVariantType = 'bold' | 'subtle';
export type BadgeSizeType = 'default' | 'small';
export type BadgeColorScheme =
  | 'neutral'
  | 'red'
  | 'magenta'
  | 'purple'
  | 'blue'
  | 'teal'
  | 'green'
  | 'yellow'
  | 'orange';

export type BadgeProps = {
  size?: BadgeSizeType;
  variant?: BadgeVariantType;
  colorScheme?: BadgeColorScheme;
} & (
  | {
      value: string | number;
      icon?: never;
    }
  | {icon: ThemeIconKey; value?: never}
) &
  TestIdProps;

const badgeVariants = cva(
  'inline-flex w-auto items-center justify-center rounded-[9999px] text-center font-[var(--font-weight-medium)] tracking-[0] [&_svg]:mx-[-4px]',
  {
    variants: {
      size: {
        default:
          'h-6 min-w-6 px-2 py-[2px] text-[length:var(--text-size-small)] leading-[var(--text-leading-small)]',
        small:
          'h-4 min-w-4 px-1 py-0 text-[length:var(--text-size-x-small)] leading-[var(--text-leading-x-small)]',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
);

const boldColorVariants = cva('', {
  variants: {
    colorScheme: {
      neutral: 'bg-[var(--color-palettes-neutral-300-100)] text-[var(--color-white)]',
      red: 'bg-[var(--color-palettes-red-60-100)] text-[var(--color-white)]',
      magenta: 'bg-[var(--color-palettes-magenta-60-100)] text-[var(--color-white)]',
      purple: 'bg-[var(--color-palettes-purple-60-100)] text-[var(--color-white)]',
      blue: 'bg-[var(--color-palettes-blue-70-100)] text-[var(--color-white)]',
      teal: 'bg-[var(--color-palettes-teal-60-100)] text-[var(--color-white)]',
      green: 'bg-[var(--color-palettes-green-60-100)] text-[var(--color-white)]',
      yellow:
        'bg-[var(--color-palettes-yellow-60-100)] text-[var(--color-palettes-neutral-800-100)]',
      orange: 'bg-[var(--color-palettes-orange-60-100)] text-[var(--color-white)]',
    },
  },
  defaultVariants: {
    colorScheme: 'green',
  },
});

const subtleColorVariants = cva('', {
  variants: {
    colorScheme: {
      neutral: 'bg-[var(--color-palettes-neutral-20-100)] text-[var(--color-primary)]',
      red: 'bg-[var(--color-palettes-red-20-100)] text-[var(--color-palettes-red-80-100)]',
      magenta:
        'bg-[var(--color-palettes-magenta-20-100)] text-[var(--color-palettes-magenta-80-100)]',
      purple: 'bg-[var(--color-palettes-purple-20-100)] text-[var(--color-palettes-purple-80-100)]',
      blue: 'bg-[var(--color-palettes-blue-20-100)] text-[var(--color-palettes-blue-80-100)]',
      teal: 'bg-[var(--color-palettes-teal-20-100)] text-[var(--color-palettes-teal-80-100)]',
      green: 'bg-[var(--color-palettes-green-20-100)] text-[var(--color-palettes-green-80-100)]',
      yellow: 'bg-[var(--color-palettes-yellow-20-100)] text-[var(--color-primary)]',
      orange: 'bg-[var(--color-palettes-orange-20-100)] text-[var(--color-palettes-orange-80-100)]',
    },
  },
  defaultVariants: {
    colorScheme: 'green',
  },
});

export function Badge(props: BadgeProps) {
  const colorVariants = props.variant === 'subtle' ? subtleColorVariants : boldColorVariants;

  return (
    <div
      className={cn(
        badgeVariants({size: props.size}),
        colorVariants({colorScheme: props.colorScheme})
      )}
      data-testid={suffixTestId('badge', props)}
    >
      <Show when={props.value}>{props.value}</Show>
      <Show when={props.icon}>
        <Icon value={props.icon} size={props.size === 'small' ? 3 : 4} />
      </Show>
    </div>
  );
}
